import { Plus, X } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { type Milestone, type MilestoneInput, useSetProgramMilestones } from "@/lib/api";
import { logger } from "@/lib/logger";
import { cn } from "@/lib/utils";

import { mondayOf, parseLocalDate } from "./dates";

interface MilestonesPanelProps {
  programMilestones: Milestone[];
  editMode: boolean;
}

/** Format a `YYYY-MM-DD` string as "Jul 14, 2026". */
function formatMilestoneDate(value: string): string {
  return parseLocalDate(value).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/** Snap a date string to its ISO-Monday week start. */
function snapToMonday(value: string): string {
  return mondayOf(parseLocalDate(value));
}

/**
 * Rebuild the FULL milestone payload from a milestone list: drop empty labels,
 * snap each date to its ISO Monday, sort by date, and assign a per-week
 * sort_order so items keep a stable column ordering after a full replace.
 */
function toMilestoneInputs(milestones: Milestone[]): MilestoneInput[] {
  const cleaned = milestones
    .map((m) => ({ week_start: snapToMonday(m.week_start), label: m.label.trim() }))
    .filter((m) => m.label.length > 0)
    .sort((a, b) => a.week_start.localeCompare(b.week_start));
  const nextOrder: Record<string, number> = {};
  return cleaned.map((m) => {
    const order = nextOrder[m.week_start] ?? 0;
    nextOrder[m.week_start] = order + 1;
    return { week_start: m.week_start, label: m.label, sort_order: order };
  });
}

/** A dedicated add/edit/remove editor for program-wide milestones on any date. */
export function MilestonesPanel({ programMilestones, editMode }: MilestonesPanelProps) {
  const setMilestones = useSetProgramMilestones();
  const [newDate, setNewDate] = useState("");
  const [newLabel, setNewLabel] = useState("");

  // Read-mode list is always date-sorted for a stable display.
  const sorted = [...programMilestones].sort((a, b) => a.week_start.localeCompare(b.week_start));

  const save = (list: Milestone[]) => {
    logger.debug("[dashboard]", "save milestones", list.length);
    setMilestones.mutate({ milestones: toMilestoneInputs(list) });
  };

  const editDate = (idx: number, week_start: string) =>
    save(programMilestones.map((m, i) => (i === idx ? { ...m, week_start } : m)));
  const editLabel = (idx: number, label: string) =>
    save(programMilestones.map((m, i) => (i === idx ? { ...m, label } : m)));
  const remove = (idx: number) => save(programMilestones.filter((_, i) => i !== idx));

  const canAdd = newDate.trim().length > 0 && newLabel.trim().length > 0;
  const add = () => {
    if (!canAdd) {
      return;
    }
    save([...programMilestones, { week_start: newDate, label: newLabel }]);
    setNewDate("");
    setNewLabel("");
  };

  return (
    <div className="border-border bg-card rounded-md border p-4">
      <h3 className="text-primary mb-3 text-[10px] font-medium uppercase">Milestones</h3>

      {editMode ? (
        <div className="space-y-2">
          {programMilestones.map((m, idx) => (
            <div key={idx} className="flex flex-wrap items-center gap-2">
              <Input
                type="date"
                value={m.week_start.slice(0, 10)}
                onChange={(e) => e.target.value && editDate(idx, e.target.value)}
                className="h-8 w-40 text-xs"
              />
              <Input
                value={m.label}
                placeholder="Milestone label"
                onChange={(e) => editLabel(idx, e.target.value)}
                className="h-8 min-w-[12rem] flex-1 text-xs"
              />
              <button
                type="button"
                onClick={() => remove(idx)}
                aria-label="Remove milestone"
                className="text-muted-foreground hover:text-destructive shrink-0"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}

          <div className="border-border flex flex-wrap items-center gap-2 border-t pt-2">
            <Input
              type="date"
              value={newDate}
              onChange={(e) => setNewDate(e.target.value)}
              className="h-8 w-40 text-xs"
            />
            <Input
              value={newLabel}
              placeholder="Add milestone"
              onChange={(e) => setNewLabel(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  add();
                }
              }}
              className="h-8 min-w-[12rem] flex-1 text-xs"
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={add}
              disabled={!canAdd}
              className="gap-1"
            >
              <Plus className="h-3.5 w-3.5" /> Add
            </Button>
          </div>
        </div>
      ) : sorted.length === 0 ? (
        <p className="text-muted-foreground text-xs">No milestones yet.</p>
      ) : (
        <ul className="space-y-1">
          {sorted.map((m, idx) => (
            <li key={idx} className="text-xs">
              <span className="text-foreground font-medium">
                {formatMilestoneDate(m.week_start)}
              </span>
              <span className="text-muted-foreground"> — </span>
              <span className={cn("text-foreground")}>{m.label}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
